import { useListSearchParams, useListState } from "../../lib/listState";
import { OrganizationAddressFields, emptyOrganizationAddress } from "../organizations/OrganizationAddressFields";
import OrganizationSettingsEditor from "../organizations/OrganizationSettingsEditor";
import OrganizationTypeField from "../organizations/OrganizationTypeField";
import { type FormEvent, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Building2,
  Cable,
  CheckCircle2,
  Download,
  FileUp,
  Pencil,
  Save,
  SlidersHorizontal,
  UserPlus,
  X
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { downloadAdminSample } from "../../lib/adminSamples";
import { ADMIN_TABULAR_IMPORT_ACCEPT, uploadAdminMasterData } from "../../lib/adminUpload";
import type {
  AdminImportType,
  OrganizationStatus,
  OrganizationSetting,
  OrganizationSettingsResponse,
  OrganizationType,
  RegistrationType,
  TechAdminOrganization,
  TechAdminUser,
  UpdateOrganizationSettingRequest,
  UserStatus
} from "../../lib/types";
import { formatDateTime, labelFromCode, statusTone } from "../shared/format";
import { RouteError, RouteLoading } from "../shared/PageState";

type AdminTab = "users" | "organizations" | "connections" | "imports";

const REGISTRATION_TYPES: RegistrationType[] = [
  "BRAND_OWNER",
  "AGENCY",
  "PRINTER",
  "RECCE",
  "DESIGNER",
  "SMB",
  "INSTALLER",
  "STORE_MANAGER",
  "PRODUCTION_TEAM"
];

const USER_ROLE_CODES = ["OWNER", "ADMIN", "PROJECT_MANAGER", "STORE_MANAGER", "RECCE", "DESIGNER", "INSTALLER"] as const;

function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : "The operation could not be completed.";
}

function MutationMessage({ error, success }: { error: unknown; success: string }) {
  if (error) {
    return <div className="pa-form-error">{errorMessage(error)}</div>;
  }
  if (success) {
    return <div className="pa-form-success"><CheckCircle2 size={16} /> {success}</div>;
  }
  return null;
}

function UsersTab({
  users,
  organizations,
  refresh,
  createOnly = false
}: {
  users: TechAdminUser[];
  organizations: TechAdminOrganization[];
  refresh: () => Promise<void>;
  createOnly?: boolean;
}) {
  const [editorOpen, setEditorOpen] = useState(createOnly);
  const Heading = createOnly ? "h1" : "h2";
  const [organizationFilter, setOrganizationFilter] = useListState("admin-users.organizationFilter", "ALL");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [mobileNumber, setMobileNumber] = useState("+91");
  const [email, setEmail] = useState("");
  const [registrationType, setRegistrationType] = useState<RegistrationType>("BRAND_OWNER");
  const [status, setStatus] = useState<UserStatus>("ACTIVE");
  const [roleCode, setRoleCode] = useState("");
  const [reportsToMembershipId, setReportsToMembershipId] = useState("");
  const [success, setSuccess] = useState("");
  const editingUser = users.find((user) => user.user_id === editingId);
  const reportingManagerOptions = editingUser?.active_organization_id
    ? users.filter((user) => (
        user.user_id !== editingUser.user_id
        && user.active_organization_id === editingUser.active_organization_id
        && Boolean(user.organization_membership_id)
        && user.status === "ACTIVE"
      ))
    : [];
  const visibleUsers = users.filter((user) => {
    if (organizationFilter === "ALL") return true;
    if (organizationFilter === "UNASSIGNED") return !user.active_organization_id;
    return user.active_organization_id === organizationFilter;
  });

  function resetForm() {
    setEditingId(null);
    setName("");
    setMobileNumber("+91");
    setEmail("");
    setRegistrationType("BRAND_OWNER");
    setStatus("ACTIVE");
    setRoleCode("");
    setReportsToMembershipId("");
  }

  function startCreate() {
    navigate("/users/new");
  }

  function closeEditor() {
    if (createOnly) { navigate("/users"); return; }
    resetForm();
    setSuccess("");
    setEditorOpen(false);
  }

  const navigate = useNavigate();

  function edit(user: TechAdminUser) {
    navigate(`/users/${user.user_id}`);
  }

  const mutation = useMutation({
    mutationFn: () => editingId
      ? api.updateAdminUser(editingId, {
          name,
          email,
          registration_type: registrationType,
          status,
          role_code: editingUser?.role_code !== roleCode ? roleCode : undefined,
          reports_to_membership_id: (editingUser?.reports_to_membership_id ?? "") !== reportsToMembershipId
            ? reportsToMembershipId
            : undefined
        })
      : api.createAdminUser({ name, mobile_number: mobileNumber, email, registration_type: registrationType }),
    onSuccess: async () => {
      const wasEditing = Boolean(editingId);
      setSuccess(wasEditing ? "User updated." : "User created and ready for organization assignment.");
      if (!wasEditing) {
        resetForm();
      }
      await refresh();
    }
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    setSuccess("");
    mutation.mutate();
  }

  return (
    <div className={createOnly ? "pa-creation-layout" : `pa-admin-entity-layout${editorOpen ? " editor-open" : ""}`}>
      {!createOnly && <section className="pa-panel pa-admin-list-panel pa-entity-list-panel pa-users-list-panel">
        <div className="pa-panel-header">
          <h2>Users</h2>
          <div className="pa-panel-header-actions">
            <span>{organizationFilter === "ALL" ? `${users.length} records` : `${visibleUsers.length} of ${users.length}`}</span>
            <select
              className="pa-header-select"
              value={organizationFilter}
              onChange={(event) => setOrganizationFilter(event.target.value)}
              aria-label="Filter users by organization"
            >
              <option value="ALL">All organizations</option>
              <option value="UNASSIGNED">Unassigned</option>
              {organizations.map((organization) => (
                <option key={organization.organization_id} value={organization.organization_id}>{organization.organization_name}</option>
              ))}
            </select>
            <button className="pa-primary-button pa-compact-button" type="button" onClick={startCreate}>
              <UserPlus size={16} /> Create user
            </button>
          </div>
        </div>
        <div className="pa-table-scroll">
          <table className="pa-table compact pa-users-table">
            <colgroup>
              <col className="pa-user-column" />
              <col className="pa-user-type-column" />
              <col className="pa-user-organization-column" />
              <col className="pa-user-status-column" />
            </colgroup>
            <thead><tr><th>User</th><th>Type</th><th>Organization</th><th>Status</th></tr></thead>
            <tbody>
              {visibleUsers.map((user) => (
                <tr
                  key={user.user_id}
                  className={editingId === user.user_id ? "selected" : undefined}
                  tabIndex={0}
                  aria-selected={editingId === user.user_id}
                  onClick={() => edit(user)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      edit(user);
                    }
                  }}
                >
                  <td><strong>{user.name || "Profile pending"}</strong><small>{user.mobile_number}{user.email ? ` · ${user.email}` : ""}</small></td>
                  <td>{user.registration_type ? labelFromCode(user.registration_type) : "Not selected"}</td>
                  <td>{user.active_organization_name || (user.eligible_organization_owner ? "Available as owner" : "Unassigned")}</td>
                  <td><span className={`pa-badge ${statusTone(user.status)}`}>{user.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>}

      {editorOpen ? (
        <section className="pa-panel pa-admin-form-panel pa-user-editor-panel">
          <div className="pa-panel-header">
            <Heading>{editingId ? <Pencil size={17} /> : <UserPlus size={17} />} <span className="pa-creation-title">{editingId ? "Edit user" : "Create user"}</span></Heading>
            <button className="pa-icon-button" type="button" title="Close user editor" aria-label="Close user editor" disabled={mutation.isPending} onClick={closeEditor}>
              <X size={16} />
            </button>
          </div>
          <form className="pa-admin-form" onSubmit={submit}>
            <label>
              <span>Name</span>
              <input value={name} onChange={(event) => setName(event.target.value)} maxLength={150} required />
            </label>
            <label>
              <span>Mobile number</span>
              <input
                value={mobileNumber}
                onChange={(event) => setMobileNumber(event.target.value)}
                placeholder="+919989274400"
                disabled={Boolean(editingId)}
                required
              />
            </label>
            <label>
              <span>Email</span>
              <input value={email} onChange={(event) => setEmail(event.target.value)} type="email" />
            </label>
            <label>
              <span>Registration type</span>
              <select value={registrationType} onChange={(event) => setRegistrationType(event.target.value as RegistrationType)}>
                {REGISTRATION_TYPES.map((type) => <option key={type} value={type}>{labelFromCode(type)}</option>)}
              </select>
            </label>
            {editingId ? (
              <>
                <label>
                  <span>Organization</span>
                  <input value={editingUser?.active_organization_name ?? "Unassigned"} readOnly />
                </label>
                <label>
                  <span>User role</span>
                  <select
                    value={roleCode}
                    onChange={(event) => setRoleCode(event.target.value)}
                    disabled={!editingUser?.organization_membership_id}
                    required={Boolean(editingUser?.organization_membership_id)}
                  >
                    <option value="">No role assigned</option>
                    {USER_ROLE_CODES.map((code) => <option key={code} value={code}>{labelFromCode(code)}</option>)}
                  </select>
                </label>
                <label>
                  <span>Reports to</span>
                  <select
                    value={reportsToMembershipId}
                    onChange={(event) => setReportsToMembershipId(event.target.value)}
                    disabled={!editingUser?.organization_membership_id}
                  >
                    <option value="">No reporting manager</option>
                    {reportingManagerOptions.map((user) => (
                      <option key={user.organization_membership_id} value={user.organization_membership_id ?? ""}>
                        {user.name || user.mobile_number}{user.role_name ? ` · ${user.role_name}` : ""}
                      </option>
                    ))}
                  </select>
                </label>
                {!editingUser?.organization_membership_id ? (
                  <p className="pa-form-note">Role and reporting manager become available after the user joins an organization.</p>
                ) : null}
                <label>
                  <span>Status</span>
                  <select value={status} onChange={(event) => setStatus(event.target.value as UserStatus)}>
                    <option value="ACTIVE">Active</option>
                    <option value="BLOCKED">Blocked</option>
                    <option value="DISABLED">Disabled</option>
                  </select>
                </label>
              </>
            ) : null}
            <MutationMessage error={mutation.error} success={success} />
            <button className="pa-primary-button" type="submit" disabled={mutation.isPending}>
              {editingId ? <Save size={17} /> : <UserPlus size={17} />}
              {mutation.isPending ? "Saving..." : editingId ? "Save user" : "Create user"}
            </button>
          </form>
        </section>
      ) : null}
    </div>
  );
}

const ORGANIZATION_IMPORT_LABELS: Record<AdminImportType, string> = {
  LOCATIONS: "Locations",
  CATALOGUE: "Catalogue",
  STAFF: "Staff"
};

function OrganizationImportSection({
  organizationId,
  importType,
  refresh
}: {
  organizationId: string;
  importType: AdminImportType;
  refresh: () => Promise<void>;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [inputKey, setInputKey] = useState(0);
  const [success, setSuccess] = useState("");
  const label = ORGANIZATION_IMPORT_LABELS[importType];
  const mutation = useMutation({
    mutationFn: () => {
      if (!file) throw new Error(`Select a ${label.toLowerCase()} spreadsheet.`);
      return uploadAdminMasterData(organizationId, importType, file);
    },
    onSuccess: async (upload) => {
      setSuccess(upload.status_message || `${upload.original_file_name} was accepted.`);
      setFile(null);
      setInputKey((current) => current + 1);
      await refresh();
    }
  });

  return (
    <section className="pa-organization-import-section">
      <div className="pa-organization-import-header">
        <div><strong>{label} spreadsheet</strong><small>{importType === "CATALOGUE" ? "Upload a CSV or Excel file with both Buy Rate and Sell Rate for every item." : "Upload a CSV or Excel file for this organization."}</small></div>
        <button className="pa-link-button" type="button" onClick={() => downloadAdminSample(importType)}>
          <Download size={15} /> Sample CSV
        </button>
      </div>
      <form onSubmit={(event) => { event.preventDefault(); setSuccess(""); mutation.mutate(); }}>
        <input
          key={inputKey}
          type="file"
          accept={ADMIN_TABULAR_IMPORT_ACCEPT}
          aria-label={`${label} spreadsheet file`}
          onChange={(event) => setFile(event.target.files?.[0] ?? null)}
          required
        />
        <button className="pa-secondary-button" type="submit" disabled={!file || mutation.isPending}>
          <FileUp size={15} /> {mutation.isPending ? "Uploading..." : "Upload"}
        </button>
      </form>
      <MutationMessage error={mutation.error} success={success} />
    </section>
  );
}

function OrganizationsTab({
  organizations,
  users,
  refresh,
  createOnly = false
}: {
  organizations: TechAdminOrganization[];
  users: TechAdminUser[];
  refresh: () => Promise<void>;
  createOnly?: boolean;
}) {
  const eligibleOwners = users.filter((user) => user.eligible_organization_owner);
  const [editorMode, setEditorMode] = useState<"create" | "edit" | null>(createOnly ? "create" : null);
  const Heading = createOnly ? "h1" : "h2";
  const [name, setName] = useState("");
  const [organizationType, setOrganizationType] = useState<OrganizationType | "">("");
  const [ownerUserId, setOwnerUserId] = useState("");
  const [billingAddress, setBillingAddress] = useState(() => emptyOrganizationAddress("BILLING"));
  const [shippingAddress, setShippingAddress] = useState(() => emptyOrganizationAddress("CLIENT_SHIPPING"));
  const [sameAsBilling, setSameAsBilling] = useState(false);
  const [shippingCopyVersion, setShippingCopyVersion] = useState(0);
  const [addressFormVersion, setAddressFormVersion] = useState(0);
  const [editingId, setEditingId] = useState("");
  const [editingName, setEditingName] = useState("");
  const [editingStatus, setEditingStatus] = useState<OrganizationStatus>("ACTIVE");
  const [createSuccess, setCreateSuccess] = useState("");
  const [updateSuccess, setUpdateSuccess] = useState("");

  const editingOrganization = organizations.find((organization) => organization.organization_id === editingId);

  function resetCreateForm() {
    setName("");
    setOrganizationType("");
    setOwnerUserId("");
    setBillingAddress(emptyOrganizationAddress("BILLING"));
    setShippingAddress(emptyOrganizationAddress("CLIENT_SHIPPING"));
    setSameAsBilling(false);
    setAddressFormVersion(version => version + 1);
  }

  function startCreate() {
    navigate("/organizations/new");
  }

  function closeEditor() {
    if (createOnly) { navigate("/organizations"); return; }
    resetCreateForm();
    setEditingId("");
    setCreateSuccess("");
    setUpdateSuccess("");
    setEditorMode(null);
  }

  const createMutation = useMutation({
    mutationFn: () => {
      if (!organizationType) throw new Error("Select an organization type.");
      const shipping = sameAsBilling ? { ...billingAddress, kind: "CLIENT_SHIPPING" as const, label: "Shipping address" } : shippingAddress;
      if (billingAddress.latitude === null || billingAddress.longitude === null) throw new Error("Choose the billing address on the map.");
      if (shipping.latitude === null || shipping.longitude === null) throw new Error("Choose the shipping address on the map.");
      return api.createAdminOrganization({ name, organization_type: organizationType, owner_user_id: ownerUserId, addresses: [billingAddress, shipping] });
    },
    onSuccess: async () => {
      resetCreateForm();
      setCreateSuccess("Organization created and the selected user assigned as owner.");
      await refresh();
    }
  });

  const updateMutation = useMutation({
    mutationFn: () => api.updateAdminOrganization(editingId, { name: editingName, status: editingStatus }),
    onSuccess: async () => {
      setUpdateSuccess("Organization updated.");
      await refresh();
    }
  });

  const navigate = useNavigate();

  function selectOrganization(id: string) {
    navigate(`/organizations/${id}`);
  }

  return (
    <div className={createOnly ? "pa-creation-layout" : `pa-admin-entity-layout${editorMode ? " editor-open" : ""}${editorMode === "create" ? " pa-org-create-layout" : ""}`}>
      {!createOnly && <section className="pa-panel pa-admin-list-panel pa-entity-list-panel pa-organizations-list-panel">
        <div className="pa-panel-header">
          <h2>Organizations</h2>
          <div className="pa-panel-header-actions">
            <span>{organizations.length} records</span>
            <button className="pa-primary-button pa-compact-button" type="button" onClick={startCreate}>
              <Building2 size={16} /> Create organization
            </button>
          </div>
        </div>
        <div className="pa-table-scroll">
          <table className="pa-table compact pa-organizations-table">
            <colgroup>
              <col className="pa-organization-name-column" />
              <col className="pa-organization-type-column" />
              <col className="pa-organization-status-column" />
            </colgroup>
            <thead><tr><th>Organization</th><th>Type</th><th>Status</th></tr></thead>
            <tbody>
              {organizations.map((organization) => (
                <tr
                  key={organization.organization_id}
                  className={editingId === organization.organization_id ? "selected" : undefined}
                  tabIndex={0}
                  aria-selected={editingId === organization.organization_id}
                  onClick={() => selectOrganization(organization.organization_id)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      selectOrganization(organization.organization_id);
                    }
                  }}
                >
                  <td><strong>{organization.organization_name}</strong><small>{organization.organization_id}</small></td>
                  <td>{labelFromCode(organization.organization_type)}</td>
                  <td><span className={`pa-badge ${statusTone(organization.status)}`}>{organization.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>}

      {editorMode === "create" ? (
        <section className="pa-panel pa-admin-form-panel pa-organization-editor-panel">
          <div className="pa-panel-header">
            <Heading><Building2 size={17} /> <span className="pa-creation-title">Create organization</span></Heading>
            <button className="pa-icon-button" type="button" title="Close organization editor" aria-label="Close organization editor" disabled={createMutation.isPending} onClick={closeEditor}>
              <X size={16} />
            </button>
          </div>
          <form className="pa-admin-form" onSubmit={(event) => { event.preventDefault(); setCreateSuccess(""); createMutation.mutate(); }}>
            <label><span>Organization name</span><input value={name} onChange={(event) => setName(event.target.value)} maxLength={150} required /></label>
            <OrganizationTypeField value={organizationType} onChange={setOrganizationType} disabled={createMutation.isPending} />
            <label>
              <span>Organization owner</span>
              <select value={ownerUserId} onChange={(event) => setOwnerUserId(event.target.value)} required>
                <option value="">Select owner</option>
                {eligibleOwners.map((user) => (
                  <option key={user.user_id} value={user.user_id}>
                    {user.name || user.mobile_number} · {user.mobile_number}
                    {user.active_organization_name ? ` · Current: ${user.active_organization_name}` : ""}
                  </option>
                ))}
              </select>
            </label>
            {eligibleOwners.length === 0 ? <p className="pa-form-note">Create an eligible active user first.</p> : null}
            <p className="pa-form-note">A billing address and a shipping address are required. These will be the defaults for this organization.</p>
            <OrganizationAddressFields key={`billing-${addressFormVersion}`} title="Billing address" value={billingAddress} onChange={setBillingAddress} disabled={createMutation.isPending} />
            <label className="pa-org-same-address"><input type="checkbox" checked={sameAsBilling} disabled={createMutation.isPending} onChange={event => setSameAsBilling(event.target.checked)} /><span>Shipping address is the same as billing</span></label>
            {sameAsBilling ? <p className="pa-form-note">The billing address will also be saved as the default shipping address.</p> : <>
              {billingAddress.latitude !== null && billingAddress.longitude !== null && <button type="button" className="pa-secondary-button" disabled={createMutation.isPending}
                onClick={() => { setShippingAddress({ ...billingAddress, kind: "CLIENT_SHIPPING", label: "Shipping address" }); setShippingCopyVersion(version => version + 1); }}>Copy billing address</button>}
              <OrganizationAddressFields key={`shipping-${addressFormVersion}-${shippingCopyVersion}`} title="Shipping address" value={shippingAddress} onChange={setShippingAddress} disabled={createMutation.isPending} />
            </>}

            <MutationMessage error={createMutation.error} success={createSuccess} />
            <button className="pa-primary-button" type="submit" disabled={createMutation.isPending || eligibleOwners.length === 0 || !organizationType}>
              <Building2 size={17} /> {createMutation.isPending ? "Creating..." : "Create organization"}
            </button>
          </form>
        </section>
      ) : null}

      {editorMode === "edit" && editingOrganization ? (
        <section className="pa-panel pa-admin-form-panel pa-organization-editor-panel">
          <div className="pa-panel-header">
            <h2><Pencil size={17} /> Organization details</h2>
            <button className="pa-icon-button" type="button" title="Close organization details" aria-label="Close organization details" onClick={closeEditor}>
              <X size={16} />
            </button>
          </div>
          <dl className="pa-entity-details">
            <div><dt>Organization ID</dt><dd>{editingOrganization.organization_id}</dd></div>
          </dl>
          <form className="pa-admin-form pa-entity-edit-form" onSubmit={(event) => { event.preventDefault(); setUpdateSuccess(""); updateMutation.mutate(); }}>
            <label><span>Name</span><input value={editingName} onChange={(event) => setEditingName(event.target.value)} required /></label>
            <label>
              <span>Status</span>
              <select value={editingStatus} onChange={(event) => setEditingStatus(event.target.value as OrganizationStatus)}>
                <option value="ACTIVE">Active</option><option value="SUSPENDED">Suspended</option><option value="DISABLED">Disabled</option>
              </select>
            </label>
            <MutationMessage error={updateMutation.error} success={updateSuccess} />
            <button className="pa-primary-button" type="submit" disabled={updateMutation.isPending}>
              <Save size={17} /> {updateMutation.isPending ? "Saving..." : "Save organization"}
            </button>
          </form>
          <OrganizationSettingsEditor organizationId={editingOrganization.organization_id} />
          <div className="pa-organization-imports">
            <OrganizationImportSection organizationId={editingOrganization.organization_id} importType="LOCATIONS" refresh={refresh} />
            <OrganizationImportSection organizationId={editingOrganization.organization_id} importType="CATALOGUE" refresh={refresh} />
            <OrganizationImportSection organizationId={editingOrganization.organization_id} importType="STAFF" refresh={refresh} />
          </div>
        </section>
      ) : null}
    </div>
  );
}

function ConnectionsTab({ organizations, refresh }: { organizations: TechAdminOrganization[]; refresh: () => Promise<void> }) {
  const activeOrganizations = organizations.filter((organization) => organization.status === "ACTIVE");
  const [organizationId, setOrganizationId] = useState("");
  const [connectedOrganizationId, setConnectedOrganizationId] = useState("");
  const [success, setSuccess] = useState("");
  const mutation = useMutation({
    mutationFn: () => api.createAdminConnection({ organization_id: organizationId, connected_organization_id: connectedOrganizationId }),
    onSuccess: async () => {
      setSuccess("Connection created. Both organizations can now discover shared workflows.");
      setOrganizationId("");
      setConnectedOrganizationId("");
      await refresh();
    }
  });
  return (
    <section className="pa-panel pa-admin-narrow-panel">
      <div className="pa-panel-header"><h2><Cable size={17} /> Connect organizations</h2></div>
      <form className="pa-admin-form" onSubmit={(event) => { event.preventDefault(); setSuccess(""); mutation.mutate(); }}>
        <label><span>First organization</span><select value={organizationId} onChange={(event) => setOrganizationId(event.target.value)} required><option value="">Select organization</option>{activeOrganizations.map((organization) => <option key={organization.organization_id} value={organization.organization_id}>{organization.organization_name} · {labelFromCode(organization.organization_type)}</option>)}</select></label>
        <label><span>Second organization</span><select value={connectedOrganizationId} onChange={(event) => setConnectedOrganizationId(event.target.value)} required><option value="">Select organization</option>{activeOrganizations.filter((organization) => organization.organization_id !== organizationId).map((organization) => <option key={organization.organization_id} value={organization.organization_id}>{organization.organization_name} · {labelFromCode(organization.organization_type)}</option>)}</select></label>
        <p className="pa-form-note">The connection is activated immediately. Existing blocks are respected and pending requests between the same organizations are closed.</p>
        <MutationMessage error={mutation.error} success={success} />
        <button className="pa-primary-button" type="submit" disabled={mutation.isPending || !organizationId || !connectedOrganizationId}><Cable size={17} /> {mutation.isPending ? "Connecting..." : "Create connection"}</button>
      </form>
    </section>
  );
}

function ImportsTab({ organizations, refresh }: { organizations: TechAdminOrganization[]; refresh: () => Promise<void> }) {
  const [organizationId, setOrganizationId] = useState("");
  const [importType, setImportType] = useState<AdminImportType>("LOCATIONS");
  const [file, setFile] = useState<File | null>(null);
  const [success, setSuccess] = useState("");
  const mutation = useMutation({
    mutationFn: () => {
      if (!file) throw new Error("Select a CSV or Excel file.");
      return uploadAdminMasterData(organizationId, importType, file);
    },
    onSuccess: async (upload) => {
      setSuccess(`${upload.original_file_name} was accepted and is being processed.`);
      setFile(null);
      await refresh();
    }
  });
  return (
    <section className="pa-panel pa-admin-narrow-panel">
      <div className="pa-panel-header"><h2><FileUp size={17} /> Upload organization master data</h2></div>
      <form className="pa-admin-form" onSubmit={(event) => { event.preventDefault(); setSuccess(""); mutation.mutate(); }}>
        <label><span>Organization</span><select value={organizationId} onChange={(event) => setOrganizationId(event.target.value)} required><option value="">Select organization</option>{organizations.map((organization) => <option key={organization.organization_id} value={organization.organization_id}>{organization.organization_name} · {labelFromCode(organization.organization_type)}</option>)}</select></label>
        <label><span>Import type</span><select value={importType} onChange={(event) => setImportType(event.target.value as AdminImportType)}><option value="LOCATIONS">Locations</option><option value="CATALOGUE">Catalogue</option></select></label>
        <label><span>Spreadsheet file</span><input type="file" accept={ADMIN_TABULAR_IMPORT_ACCEPT} onChange={(event) => setFile(event.target.files?.[0] ?? null)} required /></label>
        <p className="pa-form-note">Rows with existing store or item codes are updated by the same import processor used in the organization workspace.</p>
        {importType === "CATALOGUE" && <p className="pa-form-note">Both Buy Rate and Sell Rate are required for every item.</p>}
        <MutationMessage error={mutation.error} success={success} />
        <button className="pa-primary-button" type="submit" disabled={mutation.isPending || !organizationId || !file}><FileUp size={17} /> {mutation.isPending ? "Uploading..." : `Upload ${importType === "LOCATIONS" ? "locations" : "catalogue"}`}</button>
      </form>
    </section>
  );
}

export function CreateEntityPage({ entity }: { entity: "user" | "organization" }) {
  const queryClient = useQueryClient();
  const workspace = useQuery({ queryKey: ["tech-admin-workspace"], queryFn: () => api.getAdminWorkspace() });
  const directory = entity === "user" ? "users" : "organizations";
  async function refresh() {
    await Promise.all([
      "tech-admin-workspace", "tech-admin-audit", "tech-ops-dashboard",
      "tech-ops-organizations", "tech-ops-users", "tech-ops-user-organizations"
    ].map(key => queryClient.invalidateQueries({ queryKey: [key] })));
  }
  return <div className="pa-page-stack">
    <Link className="pa-detail-link" to={`/${directory}`}><ArrowLeft size={16} /> {entity === "user" ? "Users" : "Organizations"}</Link>
    {workspace.isPending ? <RouteLoading /> : workspace.isError ?
      <RouteError title={`Create ${entity} unavailable`} detail={errorMessage(workspace.error)} onRetry={() => workspace.refetch()} /> :
      entity === "user" ? <UsersTab key="create-user" users={workspace.data.users} organizations={workspace.data.organizations} refresh={refresh} createOnly /> :
        <OrganizationsTab key="create-organization" users={workspace.data.users} organizations={workspace.data.organizations} refresh={refresh} createOnly />}
  </div>;
}

export default function AdminPage() {
  const [searchParams, setSearchParams] = useListSearchParams();
  const selected = searchParams.get("tab") as AdminTab | null;
  const activeTab: AdminTab = ["users", "organizations", "imports"].includes(selected ?? "") ? selected! : "users";
  const queryClient = useQueryClient();
  const workspaceQuery = useQuery({ queryKey: ["tech-admin-workspace"], queryFn: () => api.getAdminWorkspace() });

  async function refresh() {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["tech-admin-workspace"] }),
      queryClient.invalidateQueries({ queryKey: ["tech-admin-audit"] }),
      queryClient.invalidateQueries({ queryKey: ["tech-ops-organizations"] }),
      queryClient.invalidateQueries({ queryKey: ["tech-ops-connections"] }),
      queryClient.invalidateQueries({ queryKey: ["tech-ops-master-data"] })
    ]);
  }

  const tabs = useMemo(() => [
    ["users", "Users"], ["organizations", "Organizations"], ["imports", "Imports"]
  ] as const, []);

  if (workspaceQuery.isLoading) return <RouteLoading />;
  if (workspaceQuery.isError || !workspaceQuery.data) {
    return <RouteError title="Admin tools unavailable" detail={errorMessage(workspaceQuery.error)} onRetry={() => workspaceQuery.refetch()} />;
  }

  const { users, organizations, generated_at: generatedAt } = workspaceQuery.data;
  return (
    <div className="pa-page-stack">
      <div className="pa-title-row">
        <div><p className="pa-eyebrow">Tech Admin</p><h1>Platform administration</h1><p>Create and maintain tenants, users and organization master data.</p></div>
        <span className="pa-refresh-chip">Updated {formatDateTime(generatedAt)}</span>
      </div>
      <div className="pa-tabs" role="tablist" aria-label="Admin tools">
        {tabs.map(([id, label]) => <button key={id} type="button" className={activeTab === id ? "active" : ""} onClick={() => setSearchParams({ tab: id })}>{label}</button>)}
      </div>
      {activeTab === "users" ? <UsersTab users={users} organizations={organizations} refresh={refresh} /> : null}
      {activeTab === "organizations" ? <OrganizationsTab organizations={organizations} users={users} refresh={refresh} /> : null}
      {activeTab === "connections" ? <ConnectionsTab organizations={organizations} refresh={refresh} /> : null}
      {activeTab === "imports" ? <ImportsTab organizations={organizations} refresh={refresh} /> : null}
    </div>
  );
}
